// Age of Steam - Data Dictionary (TypeScript)
//
// ============================================================================
// 設計方針: 型定義とマップ設定の分離
// ============================================================================
//
// - **型定義 (BarbadosActionType等)**: このファイルに集約
// - **マップ設定値 (MAP_CONFIG等)**: data/map/*/config.ts に配置
//
// 理由:
// 1. TypeScriptの慣習として型定義は types/ に一元管理するのが一般的
// 2. 型はコンパイル時のみ存在し、実行時には消える（設定値とは性質が異なる）
// 3. インポートパスをシンプルに保てる
// 4. マップ固有の型を分散させると約15ファイルの修正が必要で工数に見合わない
//
// ============================================================================

// ============================================================================
// 1. プリミティブ型
// ============================================================================

/** プレイヤーID */
export type PlayerId = string;

/** 商品色 */
export type GoodsColor = "red" | "blue" | "purple" | "yellow" | "black";

/** 地形種別 */
export type TerrainType = "plains" | "river" | "mountains";

/**
 * タイル種別（14種類）
 * 仕様書: docs/reference/appendix_tile_list.md
 * 定数定義: src/game/constants/tiles.ts
 */
export type TileType =
  // Simple (3種)
  | "STRAIGHT"
  | "ACUTE_CURVE"
  | "GENTLE_CURVE"
  // Crossing (3種)
  | "GENTLE_STRAIGHT_CROSS"
  | "TWO_GENTLE_CROSS"
  | "TWO_STRAIGHT_CROSS"
  // Coexist (4種)
  | "TWO_GENTLE_COEXIST"
  | "LEFT_GENTLE_ACUTE"
  | "RIGHT_GENTLE_ACUTE"
  | "STRAIGHT_ACUTE"
  // Town (5種)
  | "TOWN_1"
  | "TOWN_3_LEFT"
  | "TOWN_3_RIGHT"
  | "TOWN_3_HALF"
  | "TOWN_3_STAR"
  // New City (8種: A-H)
  | "NEW_CITY_A"
  | "NEW_CITY_B"
  | "NEW_CITY_C"
  | "NEW_CITY_D"
  | "NEW_CITY_E"
  | "NEW_CITY_F"
  | "NEW_CITY_G"
  | "NEW_CITY_H";

/** フェーズ種別 */
export type PhaseType =
  | "issueShares"
  | "determineOrder"
  | "selectActions"
  | "buildTrack"
  | "moveGoods"
  | "collectIncome"
  | "payExpenses"
  | "incomeReduction"
  | "refillGoods"
  | "advanceTurn";

/** アクション種別 */
export type ActionType =
  | "FIRST_MOVE"
  | "FIRST_BUILD"
  | "ENGINEER"
  | "LOCOMOTIVE"
  | "URBANIZATION"
  | "PRODUCTION"
  | "TURN_ORDER";

/** Barbados専用アクション種別 */
export type BarbadosActionType =
  | "ENGINEER"
  | "LOCOMOTIVE"
  | "URBANIZATION"
  | "PRODUCTION";

/** Puerto Rico専用アクション種別（2種類のみ） */
export type PuertoRicoActionType =
  | "ENGINEER"      // 線路を4枚まで配置可能
  | "LOCOMOTIVE";   // 機関車レベル+1（即座実行）

/** Seattle Metro Solo専用アクション種別（2種類のみ） */
export type SeattleActionType =
  | "ENGINEER"      // 線路を4枚まで配置可能
  | "LOCOMOTIVE";   // 機関車レベル+1 OR 貨物輸送レベル+1（選択）

/** Seattle Locomotive アクションの効果タイプ */
export type SeattleLocomotiveEffect =
  | "LOCO_UP"       // 機関車レベル+1
  | "CARGO_UP";     // 貨物輸送レベル+1

/** Puerto Rico 難易度 */
export type PuertoRicoDifficulty =
  | "Beginner"
  | "Novice"
  | "Expert"
  | "Master"
  | "Conqueror"
  | "God";

/** 難易度ごとのキューブ数設定 */
export const DIFFICULTY_CUBES: Record<PuertoRicoDifficulty, { red: number; black: number }> = {
  'Beginner': { red: 17, black: 5 },
  'Novice': { red: 16, black: 6 },
  'Expert': { red: 15, black: 7 },
  'Master': { red: 14, black: 8 },
  'Conqueror': { red: 13, black: 9 },
  'God': { red: 12, black: 10 }
};

/** プレイヤーカラー定義（線路・リンク所有者表示用） */
export const PLAYER_COLORS: Record<string, string> = {
  '0': '#ED8936',     // Player 0（ソロプレイヤー）: オレンジ
  '1': '#E53E3E',     // Player 1: 赤
  '2': '#3182CE',     // Player 2: 青
  '3': '#38A169',     // Player 3: 緑
  '4': '#D69E2E',     // Player 4: 黄
  '5': '#805AD5',     // Player 5: 紫
};

/** 所有者なし線路の色（濃いグレー） */
export const UNOWNED_TRACK_COLOR = '#4A5568';

// ============================================================================
// 2. 座標系
// ============================================================================

/**
 * ヘックス座標（Axial座標系）
 * @see https://www.redblobgames.com/grids/hexagons/#coordinates-axial
 */
export interface HexCoordinate {
  /** 列（Q軸） */
  q: number;
  /** 行（R軸） */
  r: number;
}

/**
 * エッジ方向（フラットトップヘックス）
 * ```
 *      0
 *   5     1
 *   4     2
 *      3
 * ```
 */
export enum HexEdge {
  TOP = 0,
  TOP_RIGHT = 1,
  BOTTOM_RIGHT = 2,
  BOTTOM = 3,
  BOTTOM_LEFT = 4,
  TOP_LEFT = 5
}

// ============================================================================
// 3. プレイヤー
// ============================================================================

/**
 * プレイヤー状態
 */
export interface Player {
  /** プレイヤーID */
  id: PlayerId;

  /** プレイヤー名 */
  name: string;

  /** プレイヤーカラー（線路所有者表示用） */
  color: string;

  /** 現金（ドル） */
  cash: number;

  /** 収入レベル */
  incomeLevel: number;

  /** 発行済み株式数 */
  sharesIssued: number;

  /** 機関車レベル（1-6リンク） */
  locomotiveLevel: number;

  /** 今ターン選択したアクション */
  selectedAction: ActionType | null;

  /** プレイ順（1位から順に小さい数値） */
  playerOrder: number;

  /** 倒産フラグ */
  isBankrupt: boolean;

  /** 今ターンのプレイ順決定で支払った金額 */
  orderBidAmount: number;

  /** 今ターン配置したタイル数（Phase 4用） */
  tilesPlacedThisTurn: number;

  /** Phase 4で手番終了を選択したか */
  hasPassedBuild: boolean;

  /** Phase 3/5でパスしたか（マップ固有）*/
  hasPassed?: boolean;

  /** 今ターンの機関車強化済みフラグ（Phase 5用） */
  hasUpgradedLocoThisTurn: boolean;

  /** Phase 5での輸送回数（最大2回） */
  goodsTransportedThisPhase: number;

  /** Phase 5で完了した手番数（0, 1, 2） */
  turnsCompletedThisPhase: number;

  /** 今ターン配置した商品数（Phase 9用、最大2個） */
  goodsPlacedThisTurn: number;

  /** 今ターン配置した新都市タイル数（Phase 4用、最大1枚） */
  newCityTilesPlacedThisTurn: number;

  /** 今ターンの線路建設履歴（Cancel用） */
  buildTrackHistory: BuildTrackHistoryEntry[];

  /** 株式発行Moveを実行済みかどうか（Phase終了判定用） */
  hasExecutedIssueSharesMove: boolean;

  /** 今ターン発行した株式数（maxSharesPerTurn 対応） */
  currentTurnSharesIssued: number;
}

/**
 * 線路建設履歴エントリ（Cancel操作のための巻き戻し情報）
 */
export interface BuildTrackHistoryEntry {
  /** 配置したヘクス座標 */
  hexCoordinate: HexCoordinate;
  /** 配置したタイル種別 */
  tileType: TileType;
  /** 回転角度 */
  rotation: 0 | 1 | 2 | 3 | 4 | 5;
  /** 配置種別 */
  placementType: 'place' | 'replace';
  /** 支払ったコスト */
  cost: number;
  /** 元のタイル情報（replace時） */
  previousTile: TrackTile | null;
  /** 元の町情報（新都市タイル配置時のみ、都市化前の状態） */
  previousTownState: {
    isUrbanized: boolean;
    newCity: Town['newCity'];
  } | null;
  /** 新都市タイルかどうか */
  isNewCityTile: boolean;
  /** 置き換え時に返却したタイルの種別（ストック復元用） */
  returnedTileType?: TileType;
  /** 特殊リンク（フェリー/隣接）かどうか */
  isSpecialLink?: boolean;
  /** 特殊リンクID（Cancel用） */
  specialLinkId?: string;
  /** 特殊リンク種別 */
  specialLinkType?: 'ferry' | 'adjacent';
  /** Town Disk を使用したかどうか（Cancel時のリセット用） */
  townDiskUsed?: boolean;
  /** Town Disk を使用した Town の ID */
  townId?: string;
}

/**
 * Barbadosプレイヤー状態
 */
export interface BarbadosPlayer extends Player {
  /** 今ターン発行した株式数（0 or 1） */
  currentTurnSharesIssued: 0 | 1;

  /** 株式発行Moveを実行済みかどうか（Phase終了判定用） */
  hasExecutedIssueSharesMove: boolean;

  /** 最終スコア（株式買い戻し後の残金、ゲーム終了前はnull） */
  finalCash: number | null;

  /** 勝利フラグ */
  isVictorious: boolean;
}

// ============================================================================
// 4. マップ要素
// ============================================================================

/**
 * 都市情報
 */
export interface City {
  /** 都市ID */
  id: string;

  /** 都市名 */
  name: string;

  /** 都市色（単一または複数） */
  color: GoodsColor | GoodsColor[];

  /** 座標 */
  coordinate: HexCoordinate;

  /** ダイス番号（商品補充時に使用、1-6） */
  diceNumber: number;

  /** 複数のダイス番号（オプション、将来の拡張用） */
  diceNumbers?: number[];

  /** ダイス背景色（white: 白背景黒文字, black: 黒背景白文字） */
  diceColor?: 'white' | 'black';

  /** 新都市タイルか */
  isNewCity: boolean;

  /** 新都市タイルID（A-H、新都市の場合のみ） */
  newCityId: string | null;
}

/**
 * 町情報
 */
export interface Town {
  /** 町ID */
  id: string;

  /** 座標 */
  coordinate: HexCoordinate;

  /** 新都市タイルで置き換え済みか */
  isUrbanized: boolean;

  /** 置き換えた新都市（ある場合） */
  newCity: City | null;
}

/**
 * ヘックス情報
 */
export interface Hex {
  /** 座標 */
  coordinate: HexCoordinate;

  /** 地形種別 */
  terrain: TerrainType;

  /** 地形コスト */
  terrainCost: number;

  /** 配置されているタイル（なければnull） */
  tile: TrackTile | null;

  /** 都市情報（都市ヘックスの場合） */
  city: City | null;

  /** 町情報（町ヘックスの場合） */
  town: Town | null;

  /** 配置されている商品キューブ */
  goods: GoodsColor[];

  /**
   * 壁がある方向のリスト
   * この方向への線路敷設は不可（マップ外と同じ扱い）
   */
  blockedEdges?: Direction[];
}

// ============================================================================
// 5. 線路
// ============================================================================

/**
 * 線路タイル形状
 */
/**
 * タイル形状（基本分類）- 非推奨
 * 注: TileTypeで詳細が定義されるため、このフィールドは段階的に廃止予定
 */
export type TileShape =
  | "straight"    // 直線: [0, 3], [1, 4], [2, 5] のいずれか
  | "curve"       // カーブ: [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0] のいずれか
  | "y_junction"; // Y字: 連続する3方向（例: [0, 1, 2]）

/**
 * Hex の6方向（0-5）- Pointy-top orientation
 */
export type Direction = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * 線路の所有権情報（複線タイル用）
 * 複線タイルでは各線路ごとに異なるプレイヤーが所有権を持つことができる
 */
export interface TrackOwnership {
  /** 所有者のプレイヤーID */
  playerId: PlayerId;

  /** connections配列内のインデックス（0=最初の線路、1=2番目の線路） */
  connectionIndex: number;
}

/**
 * 線路タイル
 */
export interface TrackTile {
  /** タイルID（ユニーク識別子） */
  id: string;

  /** タイル種別（14種類のいずれか） */
  type: TileType;

  /** 配置座標 */
  coordinate: HexCoordinate;

  /** 回転角度（60°単位、0-5） */
  rotation: 0 | 1 | 2 | 3 | 4 | 5;

  /** 所有者（単線タイルの場合、または複線でも全て同一所有者の場合）
   * 空文字列 '' は所有者なし（敷設済み線路など）を表す
   */
  owner: PlayerId | '';

  /** 配置されたターン */
  placedTurn: number;

  /**
   * 接続エッジ（回転適用後）
   * - 単線: Direction[]
   * - 複線: Direction[][]
   * 注: TILE_DEFINITIONSのconnectionsをrotationで変換した結果
   */
  connections?: Direction[] | Direction[][];

  /** タイル形状（基本分類）- 非推奨、後方互換性のため残す */
  shape?: TileShape;

  /** 町ディスク配置フラグ */
  hasTownDisk?: boolean;

  /**
   * 複線タイルの各線路の所有権
   * 置き換え時に元の線路の所有権を維持するために使用
   */
  owners?: TrackOwnership[];
}

/**
 * リンク経路上の1ステップ
 */
export interface PathSegment {
  /** ヘクス座標 */
  coord: HexCoordinate;
  /** タイル種別（都市/町はundefined） */
  tileType?: string;
  /** 使用したEdgeペア [entry, exit]（都市/町はundefined） */
  edgePair?: [number, number];
}

/**
 * 完成区間
 */
export interface CompletedLink {
  /** 区間ID */
  id: string;

  /** 始点（都市または町のID） */
  startId: string;

  /** 終点（都市または町のID） */
  endId: string;

  /** 経路（ヘックス座標のリスト）- 後方互換性のため維持 */
  path: HexCoordinate[];

  /** 経路詳細（座標、タイル種別、使用Edge） */
  pathSegments?: PathSegment[];

  /** 所有者 */
  owner: PlayerId;

  /** リンク数 */
  linkCount: number;

  /** 完成したターン */
  completedTurn: number;
}

/**
 * 未完成区間（開放端を持つ線路の端点）
 *
 * 1本の線路の「開放端」を表す。
 * - 単線タイル: 1タイルで最大1つの UnfinishedSection（片側が都市/町/他タイルに接続、片側が開放）
 * - 複線タイル: 1タイルで最大2つの UnfinishedSection（各経路ごとに開放端がある場合）
 *
 * redirect（方向転換）の対象となるのは、この UnfinishedSection が存在するタイルのみ。
 *
 * @example
 * // 直線タイル [0, 3] で、方向0が都市に接続、方向3が開放端の場合
 * {
 *   id: "section-1",
 *   coord: { q: 2, r: 1 },
 *   entryEdge: 0,   // 都市側
 *   exitEdge: 3,    // 開放端側
 *   owner: "0",
 *   createdTurn: 1,
 *   tilesAddedSinceLastTurn: 1
 * }
 */
export interface UnfinishedSection {
  /** 区間ID */
  id: string;

  /** タイルが置かれたヘクス座標 */
  coord: HexCoordinate;

  /** 入口の辺番号（接続済み側：都市/町/他タイルへ向かう方向） */
  entryEdge: Direction;

  /** 出口の辺番号（開放端側：未接続側） */
  exitEdge: Direction;

  /** 所有者 */
  owner: PlayerId;

  /** 複線タイルの場合の経路インデックス（0 or 1）*/
  connectionIndex?: number;

  /** 作成されたターン */
  createdTurn: number;

  /** 前ターンからのタイル追加数（所有権維持判定用） */
  tilesAddedSinceLastTurn: number;
}

// ============================================================================
// 6. 商品補充表
// ============================================================================

/**
 * 商品補充表の列
 *
 * 2種類の列が存在する（Issue #11-3）:
 * - 都市列: ダイス目(1-6)に対応、3行のキューブ枠
 * - 新都市列: ダイス目(3-6 or 1-4)に対応した新都市(A-H)、2行のキューブ枠
 *
 * cityId と newCityId は排他的（どちらか一方のみ設定）
 */
export interface GoodsColumn {
  /** 列番号（ダイス目: 1-6） */
  columnNumber: number;

  /** 対応都市ID（都市列の場合に設定） */
  cityId: string;

  /** 対応新都市ID（A-H、新都市列の場合に設定） */
  newCityId: string | null;

  /** 商品キューブ（上から順、nullは空きマス）
   * - 都市列: 3個 (index 0-2)
   * - 新都市列: 2個 (index 0-1)
   */
  cubes: (GoodsColor | null)[];
}

/**
 * 商品補充表（明側・暗側）
 */
export interface GoodsDisplay {
  /** 明側（左側、白数字）の商品配置 */
  lightSide: GoodsColumn[];

  /** 暗側（右側、黒数字）の商品配置 */
  darkSide: GoodsColumn[];
}

// ============================================================================
// 7. アクション選択関連
// ============================================================================

/**
 * アクション選択記録（基本ルール）
 * 各アクションに対して、選択したプレイヤーIDを記録
 */
export type SelectedActionsMap = Partial<Record<ActionType, PlayerId>>;

/**
 * アクションマーカー使用状況（Barbados専用）
 * 各アクションに対して、使用済みかどうかを記録
 */
export type ActionMarkersMap = Record<BarbadosActionType, boolean>;

// ============================================================================
// 7.5 マップ設定（マップ固有ルール）
// ============================================================================

/**
 * 支出計算式の種別
 * - 'standard': 株式 + 機関車Lv（Barbados, Puerto Rico）
 * - 'with_turn': 株式 + 機関車Lv + ターン数（Detroit）
 */
export type ExpenseFormulaType = 'standard' | 'with_turn';

/**
 * 収入減少計算式の種別
 * - 'standard': 通常ルール（10ステップごと）
 * - 'detroit': Detroit ルール（5ステップごと）
 */
export type IncomeReductionFormulaType = 'standard' | 'detroit';

/**
 * マップ固有の設定
 * 各マップで異なるルールや制約を定義
 */
export interface MapConfig {
  /** マップ名 */
  name: string;
  /** 使用可能な新都市タイル */
  availableNewCityTiles: TileType[];
  /** デフォルトのプレイヤー数 */
  defaultPlayerCount: number;
  /** ゲームの最大ターン数 */
  maxTurns: number;
  /** 1ターンに発行可能な株式数の上限（undefinedなら無制限） */
  maxSharesPerTurn?: number;
  /** 株式発行の総上限（デフォルト: 15） */
  maxSharesLimit?: number;
  /** 商品ディスプレイを使用するか（デフォルト: true） */
  hasGoodsDisplay?: boolean;
  /** エンジニアボーナス: 最安タイルを無料にする（デフォルト: false） */
  hasEngineerBonus?: boolean;
  /** 支出計算式（デフォルト: 'standard'） */
  expenseFormula?: ExpenseFormulaType;
  /** 収入減少計算式（デフォルト: 'standard'） */
  incomeReductionFormula?: IncomeReductionFormulaType;
  /** 株式発行ヘルプテキストのキー（undefinedならヘルプ非表示） */
  issueSharesHelpKey?: 'barbados' | 'detroit';
  /** ソロプレイマップか（trueなら競りフェーズをスキップ） */
  isSoloPlay?: boolean;

  /** 山岳ヘクスへのタイル敷設を許可するか（デフォルト: true） */
  canBuildOnMountains?: boolean;

  /** 貨物輸送レベル別維持費テーブル（例: {2:0, 3:4, 4:7, 5:10}） */
  cargoTransportExpenses?: Record<number, number>;

  /**
   * 敷設済み線路の定義（ゲーム開始時に配置される所有者なし線路）
   */
  preLaidTracks?: Array<{
    hexCoord: HexCoordinate;
    tileType: TileType;
    rotation: 0 | 1 | 2 | 3 | 4 | 5;
  }>;

  /**
   * Gray City（収入0の特殊都市）のIDリスト
   * これらの都市への輸送は収入が発生しない（Seattle Metro等で使用）
   */
  grayCityIds?: string[];

  /**
   * マップ表示の初期向き（デフォルト: 'portrait' = 90度回転状態）
   * - 'portrait': 縦向き（回転ボタン未押下、実際には-90度回転表示）
   * - 'landscape': 横向き（回転ボタン1回押下、回転なし表示）
   */
  initialOrientation?: 'portrait' | 'landscape';
}

// ============================================================================
// 8. Move-Level Replay Recording（ML学習用）
// ============================================================================

/** プレイヤーのMove呼び出し記録 */
export interface MoveLogEntry {
  turn: number;       // G.actualTurn
  phase: string;      // ctx.phase
  move: string;       // Move関数名 (例: "buildTrack", "issueShares")
  args?: unknown;     // Move引数 (例: { coordinate: {q:3,r:2}, tileType: "STRAIGHT", rotation: 2 })
}

/** 自動フェーズ（ダイスロール等）の記録 */
export interface AutoPhaseLogEntry {
  turn: number;
  phase: string;
  type: 'autoPhase';
  randomResults: Record<string, unknown>;  // ダイス結果など
}

/** ゲームログエントリ（Move or AutoPhase） */
export type GameLogEntry = MoveLogEntry | AutoPhaseLogEntry;

// ============================================================================
// 9. ゲーム状態（Boardgame.io の G 型）
// ============================================================================

/**
 * Age of Steam ゲーム状態（Boardgame.io の G 型）
 */
export interface AoSGameState {
  /** 使用マップ名 */
  mapName: string;

  /** マップ固有設定 */
  mapConfig: MapConfig;

  /** プレイヤー情報 */
  players: Record<PlayerId, Player>;

  /** 今ターン選択されたアクション（アクション→プレイヤーIDのマップ） */
  selectedActions?: SelectedActionsMap;

  /** マップ情報（ヘックスの配列） */
  map: Hex[];

  /** 商品補充表 */
  goodsDisplay: GoodsDisplay;

  /** 袋に残っている商品キューブ */
  cubesInBag: GoodsColor[];

  /** 除去された商品キューブ（輸送済み） */
  removedCubes: GoodsColor[];

  /** 完成区間リスト */
  completedLinks: CompletedLink[];

  /** 未完成区間リスト */
  unfinishedSections: UnfinishedSection[];

  /** フェリーリンク（離れたヘクス間の仮想リンク） */
  ferryLinks: Array<{
    id: string;
    from: HexCoordinate;
    to: HexCoordinate;
    cost: number;
    /** 所有プレイヤーID（未敷設の場合は null） */
    owner: PlayerId | null;
  }>;

  /** 隣接リンク（隣接ヘクス間の特殊コストリンク） */
  adjacentLinks: Array<{
    id: string;
    from: HexCoordinate;
    to: HexCoordinate;
    cost: number;
    /** 所有プレイヤーID（未敷設の場合は null） */
    owner: PlayerId | null;
  }>;

  /** Production アクション実行済みフラグ（Phase 9） */
  productionExecuted?: boolean;

  /** 袋から引いた商品（Production アクション選択時のみ、Phase 9） */
  drawnGoods?: [GoodsColor, GoodsColor] | null;

  /** Production配置カウント（Phase 9） */
  productionPlacementCount?: number;

  /** Production 開始時の goodsDisplay スナップショット（Cancel 用） */
  productionSnapshot?: GoodsDisplay | null;

  /** ゲーム終了フラグ */
  isGameOver: boolean;

  /** ゲーム終了理由 (Barbados: VICTORY/DEFEAT/BANKRUPTCY, Puerto Rico: VICTORY/DEFEAT_BLACK_CUBES/BANKRUPTCY, Seattle: VICTORY/DEFEAT/DEFEAT_DEMAND_OVERLOAD/DEFEAT_SETTLEMENT_FAILED/BANKRUPTCY) */
  endReason: "turn_limit" | "all_bankrupt" | "VICTORY" | "DEFEAT" | "DEFEAT_BLACK_CUBES" | "DEFEAT_DEMAND_OVERLOAD" | "DEFEAT_SETTLEMENT_FAILED" | "BANKRUPTCY" | null;

  /** 最終スコア */
  finalScores: Record<PlayerId, number>;

  /** 今ターンで完了した自動フェーズの履歴（UIアニメーション用） */
  completedAutoPhases?: string[];

  /** 現在の自動フェーズ処理結果（UIで表示後、ユーザークリックで進行） */
  autoPhaseResult?: AutoPhaseResult | null;

  /**
   * ターン開始時のスナップショット（Undo to Turn Start 用）
   * Phase 1 (issueShares) 開始時に保存し、Settings から復元可能
   * Phase 9 (refillGoods) 以降は Undo 不可
   * JSON 文字列として保存（全フィールドをディープコピー）
   */
  turnStartSnapshot?: string;

  /**
   * ターン開始時までの Undo 実行回数（ランキングメタデータ用）
   */
  undoCount: number;

  /**
   * Phase 1 (issueShares) に強制遷移するフラグ
   * undoToTurnStart Move で設定され、各 Phase の next で参照される
   * issueSharesPhase の onBegin でリセット
   */
  forcePhaseToIssueShares?: boolean;

  /**
   * Issue #22: Phase 4 開始時のスナップショット（Cancel操作用）
   * Cancel時にこの状態に復元する
   */
  buildTrackPhaseSnapshot?: {
    completedLinks: CompletedLink[];
    unfinishedSections: UnfinishedSection[];
    /** 特殊リンク所有権状態（Cancel復元用） */
    ferryLinkOwners?: Array<{ id: string; owner: PlayerId | null }>;
    adjacentLinkOwners?: Array<{ id: string; owner: PlayerId | null }>;
  };

  /**
   * Town Disk 残り枚数
   * Town タイル以外を Town ヘクスに配置する際に使用
   * 初期値: 8、各配置で -1、都市化で +1（返却）
   */
  townDisksRemaining: number;

  /**
   * Town ヘクスへの Town Disk 配置記録（townId -> boolean）
   * 各 Town ヘクスには Town タイル以外を最大 1 枚まで配置可能
   */
  townDiskPlaced: Record<string, boolean>;

  /**
   * Phase 5: 輸送回数上限（デフォルト: 2）
   * Seattle では cargoTransportLevel と同期される
   */
  maxDeliveriesThisPhase?: number;

  /**
   * Phase 4: 線路建設コスト情報（一括払い方式）
   * バックエンドで計算し、UIはこれを表示するだけ
   * マップ固有の割引計算はマップ専用Phaseで実装
   */
  buildCostInfo?: {
    /** 合計コスト（割引前） */
    totalCost: number;
    /** 割引額（エンジニアボーナス等） */
    discount: number;
    /** 最終支払額（totalCost - discount） */
    finalCost: number;
    /** 所持金オーバーフラグ */
    isOverBudget: boolean;
  };

  /** Move-Level Replay Log（ML学習用） */
  moveLog: GameLogEntry[];
}

/**
 * 自動フェーズ処理結果
 */
export interface AutoPhaseResult {
  /** フェーズ名 */
  phase: 'collectIncome' | 'payExpenses' | 'incomeReduction' | 'advanceTurn' | 'refillGoods';
  /** 表示メッセージ */
  message: string;
  /** フローティングテキスト（PlayerInfoPanel上に表示） */
  floatingText: string;
  /** 詳細情報 */
  details?: {
    income?: number;
    expenses?: number;
    dividends?: number;
    locomotiveCost?: number;
    cargoExpense?: number;  // Seattle: 貨物輸送レベル維持費
    reduction?: number;
    newTurn?: number;
    bankrupt?: boolean;
    diceRolls?: [number, number] | [number, number, number] | { light: [number, number]; dark: [number, number] };
    placements?: GoodsPlacementInfo[];
    // Seattle 固有
    skipped?: boolean;
    gameEnded?: boolean;
    drawnCubes?: GoodsColor[];
    airportCube?: GoodsColor;
    demandOverload?: boolean;
    airportOverload?: boolean;  // Airport 満杯によるゲームオーバー（Request #5）
    location?: string;
  };
}

/**
 * Barbados ゲーム状態
 */
export interface BarbadosGameState extends AoSGameState {
  /** プレイヤー情報（Barbados専用） */
  players: Record<PlayerId, BarbadosPlayer>;

  /** アクションマーカー使用状況 */
  actionMarkers?: ActionMarkersMap;

  /** アクションマーカーリセットしたターン番号 */
  actionMarkerResetTurn?: number;

  /** 実際のゲームターン数（1-10）Phase 10完了で+1 */
  actualTurn: number;

  /** 最後のダイスロール結果（Phase 9 商品補充用、アニメーション表示用） */
  lastDiceRolls?: [number, number];

  /** 最後の商品配置情報（アニメーション用） */
  lastGoodsPlacement?: GoodsPlacementInfo[];
}

/**
 * Puerto Rico プレイヤー状態
 */
export interface PuertoRicoPlayer extends Player {
  /** 今ターン選択したアクション（Puerto Rico専用：2種類 or null） */
  selectedAction: PuertoRicoActionType | null;

  /** 株式発行Moveを実行済みかどうか（Phase終了判定用） */
  hasExecutedIssueSharesMove: boolean;

  /** 最終スコア（ゲーム終了前はnull） */
  finalScore: number | null;

  /** 勝利フラグ */
  isVictorious: boolean;
}

/**
 * Puerto Rico ゲーム状態
 */
export interface PuertoRicoGameState extends Omit<AoSGameState, 'goodsDisplay'> {
  /** プレイヤー情報（Puerto Rico専用） */
  players: Record<PlayerId, PuertoRicoPlayer>;

  /** 選択した難易度 */
  difficulty: PuertoRicoDifficulty;

  /** 実際のゲームターン数（1-10）Phase 10完了で+1 */
  actualTurn: number;

  /** アクション選択完了フラグ（Phase 3終了判定用） */
  isActionSelectionComplete: boolean;

  /** 商品補充表（Puerto Rico では使用しない、常にnull） */
  goodsDisplay: GoodsDisplay | null;
}

/**
 * 商品配置情報（アニメーション用）
 */
export interface GoodsPlacementInfo {
  /** ダイス目（列番号） */
  diceValue: number;
  /** 商品色 */
  color: GoodsColor;
  /** 配置先都市名 */
  cityName: string;
  /** 配置先都市ID */
  cityId: string;
  /** 都市タイプ（city または newCity） */
  cityType: 'city' | 'newCity';
  /** サイド（light または dark） */
  side: 'light' | 'dark';
}

// ============================================================================
// 8.4 Seattle Metro Solo Types
// ============================================================================

/**
 * Seattle Metro Solo プレイヤー状態
 */
export interface SeattlePlayer extends Player {
  /** 今ターン選択したアクション（Seattle専用：2種類 or null） */
  selectedAction: SeattleActionType | null;

  /** 今ターン選択したアクションの配列（0〜2個）*/
  actionsSelectedThisTurn: SeattleActionType[];

  /** アクション選択をパスしたかどうか */
  hasPassed: boolean;

  /** Engineer アクション使用回数（清算時の支払い計算用） */
  engineerUsageCount: number;

  /** Locomotive アクション使用回数（清算時の支払い計算用） */
  locomotiveUsageCount: number;

  /** 今ターンの Locomotive 効果（Cancel 時の巻き戻し用） */
  locomotiveEffectThisTurn?: SeattleLocomotiveEffect;

  /** 株式発行Moveを実行済みかどうか（Phase終了判定用） */
  hasExecutedIssueSharesMove: boolean;

  /** Downtown/Airport に輸送し取り除いた商品キューブ数（最終得点計算用） */
  deliveredCubes: number;

  /** 最終スコア（ゲーム終了前はnull） */
  finalScore: number | null;

  /** 勝利フラグ（スコア0以上で勝利） */
  isVictorious: boolean;
}

/**
 * Seattle Metro Solo ゲーム状態
 */
export interface SeattleGameState extends Omit<AoSGameState, 'goodsDisplay'> {
  /** プレイヤー情報（Seattle専用） */
  players: Record<PlayerId, SeattlePlayer>;

  /** 貨物輸送レベル（初期2、最大5、輸送回数を決定） */
  cargoTransportLevel: number;

  /** 実際のゲームターン数（1-10）Phase 10完了で+1 */
  actualTurn: number;

  /** Downtown に留置されている商品キューブ（無制限） */
  goodsAtDowntown: GoodsColor[];

  /** Airport に留置されている商品キューブ（最大3個） */
  goodsAtAirport: GoodsColor[];

  /** 商品補充表（Seattle では使用しない、常にnull） */
  goodsDisplay: GoodsDisplay | null;

  /** 最後のダイスロール結果（Phase 9 商品補充用、3個のダイス） */
  lastDiceRolls?: [number, number, number];

  /** 最後の商品配置情報（アニメーション用） */
  lastGoodsPlacement?: SeattleGoodsPlacementInfo[];

  /** 配置待ち商品データ（Phase 9 商品補充用） */
  pendingGoodsPlacement?: {
    /** 残りのダイス目（配置で1つ消費） */
    remainingDice: number[];
    /** 残りのキューブ（配置で1つ消費） */
    remainingCubes: GoodsColor[];
  };
}

/**
 * Seattle 商品配置情報（アニメーション用）
 */
export interface SeattleGoodsPlacementInfo {
  /** ダイス目（1-6） */
  diceValue: number;
  /** 商品色 */
  color: GoodsColor;
  /** 配置先都市/町名 */
  locationName: string;
  /** 配置先都市/町ID */
  locationId: string;
  /** 配置先タイプ（city または town） */
  locationType: 'city' | 'town';
}

/**
 * Seattle ダイス出目グループ（出目→対応City/Town）
 */
export interface SeattleDiceGroup {
  /** ダイス目（1-6） */
  diceValue: number;
  /** 対応する City/Town の ID リスト */
  locationIds: string[];
}

// ============================================================================
// 8.5 Detroit Bankruptcy Solo Types
// ============================================================================

/** Detroit専用アクション種別（3種類のみ） */
export type DetroitActionType =
  | "ENGINEER"
  | "LOCOMOTIVE"
  | "URBANIZATION";

/**
 * アクションディスク状態（Detroit専用）
 * 各アクションの残りディスク数（0-3）を管理
 */
export type ActionDiskState = Record<DetroitActionType, number>;

/**
 * Detroit プレイヤー状態
 */
export interface DetroitPlayer extends Player {
  /** 今ターン選択したアクション（Detroit専用：3種類 or null） */
  selectedAction: DetroitActionType | null;

  /** アクション選択をパスしたかどうか */
  hasPassed: boolean;

  /** 株式発行Moveを実行済みかどうか（Phase終了判定用） */
  hasExecutedIssueSharesMove: boolean;

  /** 最終スコア（ゲーム終了時のターン数、ゲーム終了前はnull） */
  finalScore: number | null;

  /** 勝利フラグ（ターン7以上で破産した場合true） */
  isVictorious: boolean;
}

/**
 * Detroit Bankruptcy Solo ゲーム状態
 */
export interface DetroitGameState extends AoSGameState {
  /** プレイヤー情報（Detroit専用） */
  players: Record<PlayerId, DetroitPlayer>;

  /** アクションディスク残数（各アクション0-3枚） */
  actionDisks: ActionDiskState;

  /** 実際のゲームターン数（破産まで無制限） */
  actualTurn: number;

  /** 最後のダイスロール結果（Phase 9 商品補充用、各サイド2個ずつ計4回） */
  lastDiceRolls?: { light: [number, number]; dark: [number, number] };

  /** 最後の商品配置情報（アニメーション用） */
  lastGoodsPlacement?: GoodsPlacementInfo[];
}

/**
 * Detroit用収入減少テーブル（5ステップ制）
 * 収入レベルから減少幅を計算
 *
 * | 収入レベル | 減少値 |
 * | 0 - 5     | 0      |
 * | 6 - 10    | -1     |
 * | 11 - 15   | -2     |
 * | 16 - 20   | -3     |
 * | ...       | ...    |
 *
 * @param incomeLevel - 現在の収入レベル
 * @returns 減少値（0以上の整数）
 */
export function getIncomeReductionDetroit(incomeLevel: number): number {
  if (incomeLevel <= 5) return 0;
  // 6-10: -1, 11-15: -2, 16-20: -3, ...
  return Math.floor((incomeLevel - 1) / 5);
}

/**
 * Detroitアクション手数料計算
 * ディスク残数に応じて手数料が変わる
 *
 * @param disksRemaining - 残りディスク数（1-3）
 * @returns 手数料（$3/$2/$1）
 */
export function getDetroitActionFee(disksRemaining: number): number {
  switch (disksRemaining) {
    case 3: return 3; // 一番上のディスク
    case 2: return 2; // 真ん中のディスク
    case 1: return 1; // 一番下のディスク
    default: return 0; // ディスクがない場合（選択不可）
  }
}

// ============================================================================
// 9. Boardgame.io 統合型
// ============================================================================

/**
 * Move の引数型（例: issueShares）
 */
export interface IssueSharesArgs {
  /** 発行する株式数 */
  issueCount: number;
}

/**
 * アクション選択の引数型
 */
export interface SelectActionArgs {
  /** 選択するアクション */
  action: ActionType | BarbadosActionType | DetroitActionType | SeattleActionType;
}

/**
 * Seattle アクション選択の引数型
 */
export interface SelectActionSeattleArgs {
  /** 選択するアクション */
  action: SeattleActionType;
  /** Locomotive 選択時の効果（LOCO_UP: 機関車+1, CARGO_UP: 貨物輸送Lv+1） */
  locomotiveEffect?: SeattleLocomotiveEffect;
}

/**
 * 線路建設の引数型
 *
 * ## 配置種別（placementType）
 *
 * ### place - 新規配置
 * 空きヘクスにタイルを配置する。
 *
 * ### replace - 置き換え
 * 既存タイルを別のタイルに置き換える。以下のパターンがある:
 *
 * 1. **単線→複線**: 元の単線の所有者は問わない。元の単線の形状を維持すること。
 * 2. **単線→単線**: 自分所有の線路であること。未完成リンクの先端にあるタイルであること。
 *    タイルへの元の入口（entryEdge）は変更しないこと。
 * 3. **複線→複線**: 置き換えによりタイルの出口が変更される経路については、
 *    パターン2と同様の制約が適用される。
 */
export interface BuildTrackArgs {
  /** 配置するHexの座標（Axial座標系） */
  hexCoordinate: HexCoordinate;

  /** 配置するタイル種別（在庫管理に使用） */
  tileType: TileType;

  /** 回転角度（60°単位、0-5） */
  rotation: 0 | 1 | 2 | 3 | 4 | 5;

  /** 配置種別 */
  placementType: "place" | "replace";
}

/**
 * 商品輸送の引数型
 */
export interface MoveGoodsArgs {
  /** 輸送する商品が現在配置されている都市ID */
  fromCityId: string;

  /** 輸送する商品の色 */
  goodsColor: GoodsColor;

  /** 目的都市ID（商品の色と一致する都市） */
  toCityId: string;

  /** 使用する経路（リンクIDの配列）- 指定しない場合は自動計算（最長経路） */
  path?: string[];
}

/**
 * Seattle 商品輸送の引数型
 */
export interface MoveGoodsSeattleArgs extends MoveGoodsArgs {
  /** Gray City 輸送時の選択（Gray City 以外では無視） */
  grayCityAction?: 'deliver' | 'keep';
}

/**
 * Production アクション実行時の引数
 * @see docs/specifications/logic/common/phase_09_goods_growth.md Section 2.2
 */
export interface ProductionArgs {
  /** 配置する商品のリスト（2個） */
  placements: ProductionPlacement[];
}

/**
 * Production で配置する1個の商品情報
 */
export interface ProductionPlacement {
  /** 明側 or 暗側 */
  side: 'light' | 'dark';

  /** 列番号（1-6） */
  column: number;

  /**
   * 列タイプ（都市列 or 新都市列）
   * - 'city': 都市列（1-6）- デフォルト
   * - 'newCity': 新都市列（A-H）
   *
   * 同じ columnNumber を持つ都市列と新都市列を区別するために使用
   */
  columnType?: 'city' | 'newCity';

  /** 行番号（0-4、上から順） */
  row: number;

  /** 商品色 */
  color: GoodsColor;
}

/**
 * 特殊リンク（フェリー/隣接）敷設の引数型
 * Detroit Bankruptcy Solo 専用
 */
export interface BuildSpecialLinkArgs {
  /** リンク種別 */
  linkType: 'ferry' | 'adjacent';

  /** リンクID（ferry-0, adjacent-1 など） */
  linkId: string;
}

// ============================================================================
// 10. Phase 2: プレイ順決定関連型
// ============================================================================

/**
 * 競りアクション種別
 */
export type AuctionActionType = "BID" | "PASS" | "DROP_OUT";

/**
 * 入札アクション
 */
export interface BidAction {
  type: "BID";
  /** 入札額（$1以上） */
  amount: number;
}

/**
 * パスアクション（Turn Orderアクション所持時のみ）
 */
export interface PassAction {
  type: "PASS";
}

/**
 * 降りるアクション
 */
export interface DropOutAction {
  type: "DROP_OUT";
}

/**
 * プレイ順決定入力
 */
export type DetermineOrderInput = BidAction | PassAction | DropOutAction;

/**
 * 競りの状態管理
 */
export interface AuctionState {
  /** 現在の最高入札額 */
  currentHighestBid: number;

  /** 現在の最高入札者 */
  highestBidder: PlayerId | null;

  /** 競りに残っているプレイヤーID（順番） */
  remainingPlayers: PlayerId[];

  /** 降りたプレイヤーID（降りた順） */
  droppedOutPlayers: PlayerId[];

  /** 各プレイヤーの直近の宣言額 */
  declaredAmounts: Record<PlayerId, number>;

  /** Turn Orderパスを使用したプレイヤー */
  passUsedBy: PlayerId[];

  /** 現在の入札者インデックス */
  currentBidderIndex: number;
}

/**
 * 競り拡張されたゲーム状態
 */
export interface AoSGameStateWithAuction extends AoSGameState {
  /** 競りの状態（Phase 2 中のみ有効） */
  auction?: AuctionState;
}

// ============================================================================
// 11. Phase 4: 線路建設用 定数・ユーティリティ関数
// ============================================================================

/**
 * 地形ごとの線路建設コスト（基本ルール、Single タイル）
 */
export const TERRAIN_COSTS: Record<TerrainType, number> = {
  plains: 2,     // 平地: $2
  mountains: 4,  // 山岳: $4
  river: 3,      // 川: $3
};

/**
 * TileType から TileShape への変換
 * @deprecated TileShapeは段階的に廃止予定。TILE_DEFINITIONSを使用してください。
 */
export function mapTypeToShape(type: TileType): TileShape {
  // 14種類のTileTypeに対応（簡易実装）
  if (type.includes('STRAIGHT') || type === 'TOWN_1') {
    return "straight";
  } else if (type.includes('CURVE') || type.includes('ACUTE') || type.includes('GENTLE')) {
    return "curve";
  } else if (type.startsWith('TOWN_3')) {
    return "y_junction";
  }
  // デフォルト
  return "straight";
}

/**
 * TileShape から出口数を計算
 */
export function getExitCount(shape: TileShape): number {
  switch (shape) {
    case "straight":
      return 2;  // 直線: 2方向
    case "curve":
      return 2;  // カーブ: 2方向
    case "y_junction":
      return 3;  // Y字: 3方向
  }
}

/**
 * 機関車維持費テーブル（Phase 7: 支払フェーズ）
 *
 * Age of Steam 基本ルール:
 * 経費 = 発行株式数 + 機関車リンク数
 * つまり、機関車維持費 = 機関車レベルそのまま
 */
export const LOCOMOTIVE_COSTS: Record<number, number> = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6
};

/**
 * 支出合計を計算（Phase 7 と Info Panel で共通使用）
 *
 * MapConfig の設定に基づき、マップ固有の追加経費を自動反映する。
 * - standard: 株式配当 + 機関車維持費
 * - with_turn: + ターン数
 * - cargoTransportExpenses: + 貨物輸送レベル維持費
 */
export function computeTotalExpenses(
  G: { players: Record<string, Player>; mapConfig: MapConfig }
): number {
  const player = G.players['0'];
  if (!player) return 0;

  const dividends = player.sharesIssued;
  const locomotiveCost = LOCOMOTIVE_COSTS[player.locomotiveLevel] || 0;
  let total = dividends + locomotiveCost;

  const mapConfig = G.mapConfig;
  if (mapConfig?.expenseFormula === 'with_turn' && 'actualTurn' in G) {
    total += (G as { actualTurn: number }).actualTurn || 1;
  }
  if (mapConfig?.cargoTransportExpenses && 'cargoTransportLevel' in G) {
    total += mapConfig.cargoTransportExpenses[(G as { cargoTransportLevel: number }).cargoTransportLevel] || 0;
  }

  return total;
}

/**
 * 収入減少テーブル（Phase 8: 収入減少フェーズ）
 * 現在の収入レベルから減少幅を取得
 */
export function getIncomeReduction(incomeLevel: number): number {
  if (incomeLevel >= 50) return 10;
  if (incomeLevel >= 41) return 8;
  if (incomeLevel >= 31) return 6;
  if (incomeLevel >= 21) return 4;
  if (incomeLevel >= 11) return 2;
  return 0;  // 0～10の範囲では減少なし
}

/**
 * 6方向の隣接Hex座標オフセット（Pointy-top orientation）
 */
const AXIAL_DIRECTIONS: Record<Direction, HexCoordinate> = {
  0: { q: +1, r: -1 },  // NE
  1: { q: +1, r:  0 },  // E
  2: { q:  0, r: +1 },  // SE
  3: { q: -1, r: +1 },  // SW
  4: { q: -1, r:  0 },  // W
  5: { q:  0, r: -1 },  // NW
};

/**
 * 指定した方角の隣接Hex座標を取得
 */
export function getAdjacentHex(
  coord: HexCoordinate,
  direction: Direction
): HexCoordinate {
  const offset = AXIAL_DIRECTIONS[direction];
  return {
    q: coord.q + offset.q,
    r: coord.r + offset.r
  };
}

/**
 * 逆方向を取得
 */
export function getOppositeDirection(direction: Direction): Direction {
  return ((direction + 3) % 6) as Direction;
}

/**
 * Hex座標を文字列キーに変換
 */
export function coordToKey(coord: HexCoordinate): string {
  return `${coord.q},${coord.r}`;
}

/**
 * 文字列キーをHex座標に変換
 */
export function keyToCoord(key: string): HexCoordinate {
  const [q, r] = key.split(',').map(Number);
  return { q, r };
}

/**
 * 2つの座標が等しいか判定
 */
export function coordsEqual(a: HexCoordinate, b: HexCoordinate): boolean {
  return a.q === b.q && a.r === b.r;
}

// ============================================================================
// 12. UI Interface Types (Re-export)
// ============================================================================

// UI層との接続用型定義
export * from './ui-interfaces';
